"use client"

import { FC, useRef } from "react"
import { Content } from "@prismicio/client"
import { PrismicRichText, SliceComponentProps } from "@prismicio/react"
import {
  LazyMotion,
  domAnimation,
  m,
  motion,
  useScroll,
  useTransform,
  useSpring,
} from "motion/react"

export type HighlightedFactCardProps =
  SliceComponentProps<Content.HighlightedFactCardSlice>

// Concentric ring SVG decoration
function RingMandala({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 200" className={className} aria-hidden>
      {[16, 32, 50, 68, 86].map((r, i) => (
        <circle
          key={i}
          cx="100"
          cy="100"
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={i % 2 === 0 ? 0.8 : 0.4}
          strokeDasharray={i % 2 === 0 ? "4 6" : "2 8"}
          opacity={0.5 - i * 0.07}
        />
      ))}
      {/* 8 spokes */}
      {Array.from({ length: 8 }, (_, i) => {
        const rad = (i * Math.PI) / 4
        return (
          <line
            key={`s-${i}`}
            x1={100 + 18 * Math.cos(rad)}
            y1={100 + 18 * Math.sin(rad)}
            x2={100 + 84 * Math.cos(rad)}
            y2={100 + 84 * Math.sin(rad)}
            stroke="currentColor"
            strokeWidth="0.4"
            opacity="0.3"
          />
        )
      })}
      <circle cx="100" cy="100" r="5" fill="currentColor" opacity="0.6" />
    </svg>
  )
}

const HighlightedFactCard: FC<HighlightedFactCardProps> = ({ slice }) => {
  const { primary } = slice
  const cardRef = useRef<HTMLDivElement>(null)

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  })

  // Card lifts slightly as it enters view
  const cardY = useTransform(scrollYProgress, [0, 0.4, 1], [60, 0, -20])
  const smoothCardY = useSpring(cardY, { stiffness: 55, damping: 18 })

  // Mandala rings rotate slowly in opposite directions
  const ringRotate = useTransform(scrollYProgress, [0, 1], [0, 120])
  const ringRotateRev = useTransform(scrollYProgress, [0, 1], [0, -90])

  // Subtle scale on the card as it enters
  const cardScale = useTransform(scrollYProgress, [0, 0.35], [0.92, 1])
  const smoothScale = useSpring(cardScale, { stiffness: 55, damping: 18 })

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        aria-label={slice.slice_type
          .split("_")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")}
        className="relative overflow-hidden section-py"
      >
        {/* Faint full-bleed radial glow behind card */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 60% at 50% 55%, oklch(0.85 0.09 42 / 0.25) 0%, transparent 70%)",
          }}
        />

        <div className="container" ref={cardRef}>
          <m.div
            className="relative mx-auto max-w-2xl"
            style={{ y: smoothCardY, scale: smoothScale }}
          >
            {/* Outer mandala ring — slow CW */}
            <m.div
              aria-hidden
              className="pointer-events-none absolute -inset-12 text-sacred-orange/20"
              style={{ rotate: ringRotate }}
            >
              <RingMandala className="size-full" />
            </m.div>

            {/* Inner mandala ring — slow CCW */}
            <m.div
              aria-hidden
              className="pointer-events-none absolute -inset-6 text-sacred-gold/25"
              style={{ rotate: ringRotateRev }}
            >
              <RingMandala className="size-full" />
            </m.div>

            {/* ── Card shell ── */}
            <div
              className="relative overflow-hidden rounded-3xl"
              style={{
                background:
                  "linear-gradient(145deg, oklch(0.62 0.2 42) 0%, oklch(0.52 0.22 32) 55%, oklch(0.40 0.2 22) 100%)",
                boxShadow:
                  "0 32px 80px oklch(0.45 0.2 32 / 0.45), 0 0 0 1px oklch(0.75 0.18 55 / 0.25) inset",
              }}
            >
              {/* Top gold shimmer line */}
              <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-px"
                style={{
                  background:
                    "linear-gradient(to right, transparent, oklch(0.88 0.18 80) 30%, oklch(0.95 0.15 70) 50%, oklch(0.88 0.18 80) 70%, transparent)",
                }}
              />

              {/* Subtle inner radial highlight */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 80% 50% at 50% 0%, oklch(1 0 0 / 0.12) 0%, transparent 60%)",
                }}
              />

              {/* Corner OM glyphs */}
              <span
                aria-hidden
                className="pointer-events-none absolute top-3 left-4 text-2xl font-bold text-white/10 select-none"
              >
                ॐ
              </span>
              <span
                aria-hidden
                className="pointer-events-none absolute right-4 bottom-3 text-2xl font-bold text-white/10 select-none"
              >
                ॐ
              </span>

              {/* ── Content ── */}
              <div className="relative flex flex-col items-center gap-6 px-8 py-12 text-center">
                {/* Emoji icons — each bounces in */}
                {primary.icons && primary.icons.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-3">
                    {primary.icons.map((item, i) => (
                      <m.span
                        key={i}
                        className="flex size-14 items-center justify-center rounded-full text-3xl leading-none"
                        style={{
                          background: "oklch(1 0 0 / 0.12)",
                          boxShadow: "0 0 0 1px oklch(1 0 0 / 0.15) inset",
                        }}
                        initial={{ opacity: 0, scale: 0.5, y: -12 }}
                        whileInView={{
                          opacity: 1,
                          scale: 1,
                          y: 0,
                          transition: {
                            type: "spring",
                            stiffness: 320,
                            damping: 12,
                            delay: i * 0.1,
                          },
                        }}
                        viewport={{ once: true, margin: "-60px" }}
                        whileHover={{ scale: 1.18, rotate: [0, -8, 8, 0] }}
                      >
                        {item.icon_emoji}
                      </m.span>
                    ))}
                  </div>
                )}

                {/* Divider — expands in */}
                <m.div
                  className="h-px w-24 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to right, transparent, oklch(0.92 0.16 75), transparent)",
                  }}
                  initial={{ scaleX: 0, opacity: 0 }}
                  whileInView={{
                    scaleX: 1,
                    opacity: 1,
                    transition: { duration: 0.8, ease: "easeOut", delay: 0.2 },
                  }}
                  viewport={{ once: true, margin: "-60px" }}
                />

                {/* Text */}
                {primary.text && (
                  <m.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.65,
                        ease: "easeOut",
                        delay: 0.15,
                      },
                    }}
                    viewport={{ once: true, margin: "-60px" }}
                    className="[&_h1]:[-webkit-text-fill-color:white] [&_h1]:[background:none] [&_h2]:[-webkit-text-fill-color:white] [&_h2]:[background:none] [&_h3]:[-webkit-text-fill-color:white] [&_h3]:[background:none]"
                  >
                    <PrismicRichText
                      field={primary.text}
                      components={{
                        heading1: ({ children }) => (
                          <h1 className="font-black text-white drop-shadow-sm">
                            {children}
                          </h1>
                        ),
                        heading2: ({ children }) => (
                          <h2 className="mt-6 font-black text-white drop-shadow-sm sm:mt-7 md:mt-8 lg:mt-9 xl:mt-10">
                            {children}
                          </h2>
                        ),
                        heading3: ({ children }) => (
                          <h3 className="font-extrabold text-white drop-shadow-sm">
                            {children}
                          </h3>
                        ),
                        paragraph: ({ children }) => (
                          <p className="mt-2 text-lg/relaxed text-white/85">
                            {children}
                          </p>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-bold text-amber-200">
                            {children}
                          </strong>
                        ),
                      }}
                    />
                  </m.div>
                )}

                {/* Bottom divider */}
                <m.div
                  className="h-px w-16 rounded-full"
                  style={{
                    background:
                      "linear-gradient(to right, transparent, oklch(0.92 0.16 75), transparent)",
                  }}
                  initial={{ scaleX: 0, opacity: 0 }}
                  whileInView={{
                    scaleX: 1,
                    opacity: 1,
                    transition: { duration: 0.8, ease: "easeOut", delay: 0.4 },
                  }}
                  viewport={{ once: true, margin: "-60px" }}
                />

                {/* Sacred dot row */}
                <div aria-hidden className="flex items-center gap-2">
                  {[8, 5, 3, 5, 8].map((size, i) => (
                    <motion.div
                      key={i}
                      className="rounded-full"
                      style={{
                        width: size,
                        height: size,
                        background: "oklch(0.92 0.16 75 / 0.6)",
                      }}
                      animate={{ opacity: [0.3, 0.9, 0.3] }}
                      transition={{
                        duration: 2.2,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.22,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Bottom gold shimmer line */}
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-px"
                style={{
                  background:
                    "linear-gradient(to right, transparent, oklch(0.88 0.18 80) 30%, oklch(0.95 0.15 70) 50%, oklch(0.88 0.18 80) 70%, transparent)",
                }}
              />
            </div>
          </m.div>
        </div>
      </section>
    </LazyMotion>
  )
}

export default HighlightedFactCard
